import { createAsyncThunk } from "@reduxjs/toolkit"

import { ErrorsActions } from "../../../Redux/redux/errors.slice"
import { handleApiError } from "../../../Utils/thunkHelpers"
import utils from "../../../Utils/utils"
import { getBusinessAccountsWithRole, getUserInfo } from "../../User/redux/user.thunks"
import * as services from "../services"
import { SETTING_SLICE_NAME } from "./setting.constants"

/* ------------------------------------------------------------------ *
 * Languages / stripe portal
 *
 * The legacy saga also had `getNotificationsList`/`changeNotificationsList`/`getUserCountry`
 * eventsOptions entries, routed through the same local `apiGenerator`. Dropped here, not
 * ported: `/user/notifications/` and `/user/get_country/` have no route at all in
 * chargeback_back/user/urls.py (never existed as real endpoints), and neither
 * `getUserCountryRequest` nor `changeNotificationsListRequest` is dispatched anywhere in this
 * repo — dead boilerplate on both the frontend and backend, not just an unused RTK branch.
 * ------------------------------------------------------------------ */

export const getLanguagesList = createAsyncThunk(
  `${SETTING_SLICE_NAME}/get_languages_list`,
  async (_, { rejectWithValue }) => {
    const response = await services.getLanguagesList()
    if (!response.data) return rejectWithValue("some error")
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

// chargeback_back's stripe_portal (create_stripe_billing_portal_session) always answers
// `{ url }` with no `redirect_url`/`id` — only the hard-navigation branch ever applies.
export const navToStripe = createAsyncThunk(
  `${SETTING_SLICE_NAME}/nav_to_stripe`,
  async (returnUrl, { dispatch, rejectWithValue }) => {
    const response = await services.navToStripe(returnUrl)
    if (!response.data) return rejectWithValue("some error")
    if (!response.ok) {
      dispatch(ErrorsActions.errorSave(response.data))
      return rejectWithValue(response.data)
    }
    if (response.data?.url) window.location.href = response.data.url
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Users management
 * ------------------------------------------------------------------ */

export const getUsersManagementRoles = createAsyncThunk(
  `${SETTING_SLICE_NAME}/get_users_management_roles`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getUsersManagementRoles()
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getUsersManagement = createAsyncThunk(
  `${SETTING_SLICE_NAME}/get_users_management`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUsersManagement(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// Custom saga logic (createInviteUserSaga): bespoke error-message extraction kept verbatim —
// it isn't equivalent to getApiErrorMessage (it indexes `.detail[0]` even when `.detail` is a
// string, and chargeback_back's create_invite_to_business_account validation errors actually
// come back under `details` (plural), not `detail`, so this rarely resolves to a clean string;
// that mismatch is pre-existing on both sides, not something to fix here).
export const createInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/create_invite_user`,
  async ({ business_account_id, onSuccess, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.createInviteUserManagement(params)
    if (!response.ok || !response.data) {
      const errorMessage =
        response.data?.message?.detail || response.data?.message || response.data?.detail?.[0]
      dispatch(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
      return rejectWithValue(errorMessage)
    }
    if (typeof onSuccess === "function") onSuccess(response.data)
    dispatch(getUsersManagement({ business_account_id }))
    return response.data
  }
)

// Custom saga logic (updateInviteUserSaga) — same bespoke error extraction as createInviteUser above.
export const updateInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/update_invite_user`,
  async ({ business_account_id, onSuccess, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.updateInviteUserManagement(params)
    if (!response.ok || !response.data) {
      const errorMessage =
        response.data?.message?.detail || response.data?.message || response.data?.detail?.[0]
      dispatch(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
      return rejectWithValue(errorMessage)
    }
    if (typeof onSuccess === "function") onSuccess(response.data)
    dispatch(getUsersManagement({ business_account_id }))
    dispatch(getBusinessAccountsWithRole())
    dispatch(getUserInfo())
    return response.data
  }
)

export const resendInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/resend_invite_user`,
  async (userId, { dispatch, rejectWithValue }) => {
    const response = await services.resendInviteUserManagement(userId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// chargeback_back's remove_invited_user answers 204 but still renders a JSON body
// (`{ details }`, no `id`) — falls into apiGeneratorBase's `response.data` branch, not its
// empty-body 204 fallback, so `removeUserSuccess`'s `user?.id || user` filter never actually
// matches an id from the response; `|| userId` reproduces that same fallback-to-params path.
export const removeUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/remove_user`,
  async (userId, { dispatch, rejectWithValue }) => {
    const response = await services.removeUserManagement(userId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(getBusinessAccountsWithRole())
    dispatch(getUserInfo())
    return response.data || userId
  }
)

export const getInviteUserInfo = createAsyncThunk(
  `${SETTING_SLICE_NAME}/get_invite_user_info`,
  async (inviteId, { dispatch, rejectWithValue }) => {
    const response = await services.getInviteUserInfo(inviteId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// chargeback_back's decline_invite is the one invite action that can answer a `redirect_url`
// (when declining leaves the user without a finished business account) — the external-redirect
// branch of apiGeneratorBase is genuinely reachable here, unlike the other invite actions.
export const rejectInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/reject_invite_user`,
  async (inviteId, { dispatch, rejectWithValue }) => {
    const response = await services.rejectInviteUser(inviteId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    if (response.data?.redirect_url) utils.goLink(response.data.redirect_url)
    dispatch(getUserInfo())
    return response.data
  }
)

export const acceptInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/accept_invite_user`,
  async (inviteId, { dispatch, rejectWithValue }) => {
    const response = await services.acceptInviteUser(inviteId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(getBusinessAccountsWithRole())
    dispatch(getUserInfo())
    return response.data
  }
)

export const logoutInviteUser = createAsyncThunk(
  `${SETTING_SLICE_NAME}/logout_invite_user`,
  async (inviteId, { dispatch, rejectWithValue }) => {
    const response = await services.logoutInviteUser(inviteId)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)
